/**
 * Persoonlijke werkruimte-tabs — DTO's + Zod-schemas.
 *
 * Conform docs/specs/21-autorisatie-navigatie-werkruimtes.md §5/§6. Drie tab-
 * typen (PAGE, DASHBOARD, FILTERED_VIEW), per-user, plus functie-templates.
 * De API-routes (`/me/workspace-tabs*`) + `initWorkspaceFromTemplates` komen in
 * PR #D; deze module levert de contracten.
 */

import { z } from 'zod'
import type { FunctionType } from './authorization'

/// Tab-type (spec 21 §5). Mirror van Prisma `WorkspaceTabType`.
export const WORKSPACE_TAB_TYPES = ['PAGE', 'DASHBOARD', 'FILTERED_VIEW'] as const

export type WorkspaceTabType = (typeof WORKSPACE_TAB_TYPES)[number]

// ─── DTO's ───────────────────────────────────────────────────────────────────

export interface UserWorkspaceTabDTO {
  id: string
  type: WorkspaceTabType
  label: string
  path: string | null
  config: Record<string, unknown> | null
  sortOrder: number
  pinned: boolean
  dashboardId: string | null
}

export interface WorkspaceTemplateDTO {
  id: string
  function: FunctionType
  label: string
  type: WorkspaceTabType
  path: string | null
  config: Record<string, unknown> | null
  sortOrder: number
  pinned: boolean
}

// ─── Zod-schemas (spec 21 §6) ────────────────────────────────────────────────

// Pad-validatie: relatief pad startend met "/", optioneel met query-params.
// Identiek patroon als de spec-listing (§6) zodat alleen geldige routes worden
// opgeslagen. Geëxporteerd zodat de backoffice-UI (WorkspaceTabsManager én de
// "+"-popover op de tab-balk, FO-057) exact dezelfde validatie client-side draait
// — één waarheid, geen gedupliceerde regex.
export const workspacePathSchema = z
  .string()
  .max(500)
  .regex(/^\/[a-zA-Z0-9\-_/?=&%.,]*$/)
  // Weiger protocol-relatieve URL's ("//host"): die matchen het patroon wél
  // (begint met "/", bevat alleen toegestane tekens) maar navigeren extern.
  // FO-057 §6 "externe URL's worden geweigerd" — ook server-side afgedwongen.
  .refine((v) => !v.startsWith('//'), {
    message: 'Externe links zijn niet toegestaan; gebruik een relatief pad (begint met "/").',
  })

export const workspaceTabCreateSchema = z.object({
  type: z.enum(WORKSPACE_TAB_TYPES),
  label: z.string().min(1).max(100),
  path: workspacePathSchema.optional(),
  config: z.record(z.string(), z.unknown()).optional(),
  pinned: z.boolean().optional(),
})

export type WorkspaceTabCreateInput = z.infer<typeof workspaceTabCreateSchema>

export const workspaceTabUpdateSchema = z.object({
  label: z.string().min(1).max(100).optional(),
  path: workspacePathSchema.optional(),
  config: z.record(z.string(), z.unknown()).optional(),
  sortOrder: z.number().int().min(0).optional(),
  pinned: z.boolean().optional(),
})

export type WorkspaceTabUpdateInput = z.infer<typeof workspaceTabUpdateSchema>

export const workspaceTabReorderSchema = z.object({
  tabIds: z.array(z.string()).min(1).max(50),
})

export type WorkspaceTabReorderInput = z.infer<typeof workspaceTabReorderSchema>
